import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  SiteRisk,
  SiteRiskListParams,
  SiteRiskListResponse,
  KRIAlertListParams,
  KRIAlertListResponse,
  UpdateKRIAlertRequest,
  MonitoringQueueParams,
  MonitoringQueueResponse,
  RBQMDashboardData,
  CreateActionRequest,
  CreateActionResponse,
  SiteDetailData,
  KRIConfiguration,
  UpdateKRIConfigRequest,
} from "../types/rbqm.type";

// ─── Helper ──────────────────────────────────────────────────────────────────

function assertSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): asserts response is ApiResponse<T> & { data: T } {
  const ok = response.statusCode >= 200 && response.statusCode < 300;
  if (!ok) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
  if (response.data === undefined) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
}

function assertRequestSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): void {
  const ok = response.statusCode >= 200 && response.statusCode < 300;
  if (!ok) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
}

// ─── Interface ───────────────────────────────────────────────────────────────

export interface IRBQMRepository {
  // Dashboard
  getDashboard(studyId: string): Promise<RBQMDashboardData>;

  // Site Risk
  getSiteRisks(
    studyId: string,
    params?: SiteRiskListParams,
  ): Promise<SiteRiskListResponse>;
  getSiteRiskById(studyId: string, siteId: string): Promise<SiteRisk>;
  getSiteDetail(studyId: string, siteId: string): Promise<SiteDetailData>;

  // KRI Alerts
  getKRIAlerts(
    studyId: string,
    params?: KRIAlertListParams,
  ): Promise<KRIAlertListResponse>;
  updateKRIAlert(
    studyId: string,
    alertId: string,
    data: UpdateKRIAlertRequest,
  ): Promise<void>;

  // Monitoring Queue
  getMonitoringQueue(
    studyId: string,
    params?: MonitoringQueueParams,
  ): Promise<MonitoringQueueResponse>;

  // Actions
  createAction(
    studyId: string,
    data: CreateActionRequest,
  ): Promise<CreateActionResponse>;

  // KRI Configuration
  getKRIConfigurations(studyId: string): Promise<KRIConfiguration[]>;
  updateKRIConfiguration(
    studyId: string,
    configId: string,
    data: UpdateKRIConfigRequest,
  ): Promise<void>;
}

// ─── Implementation ──────────────────────────────────────────────────────────

export class RBQMRepository implements IRBQMRepository {
  // ─── Dashboard ─────────────────────────────────────────────────────────────

  async getDashboard(studyId: string): Promise<RBQMDashboardData> {
    const response = await httpCoreService.get<RBQMDashboardData>(
      `/studies/${studyId}/rbqm/dashboard`,
    );
    assertSuccess(response, "Failed to load RBQM dashboard");
    return response.data;
  }

  // ─── Site Risk ─────────────────────────────────────────────────────────────

  async getSiteRisks(
    studyId: string,
    params?: SiteRiskListParams,
  ): Promise<SiteRiskListResponse> {
    const response = await httpCoreService.get<SiteRiskListResponse>(
      `/studies/${studyId}/rbqm/site-risks`,
      { params },
    );
    assertSuccess(response, "Failed to load site risks");
    return response.data;
  }

  async getSiteRiskById(studyId: string, siteId: string): Promise<SiteRisk> {
    const response = await httpCoreService.get<SiteRisk>(
      `/studies/${studyId}/rbqm/site-risks/${siteId}`,
    );
    assertSuccess(response, "Failed to load site risk");
    return response.data;
  }

  async getSiteDetail(
    studyId: string,
    siteId: string,
  ): Promise<SiteDetailData> {
    const response = await httpCoreService.get<SiteDetailData>(
      `/studies/${studyId}/rbqm/site-risks/${siteId}/detail`,
    );
    assertSuccess(response, "Failed to load site detail");
    return response.data;
  }

  // ─── KRI Alerts ────────────────────────────────────────────────────────────

  async getKRIAlerts(
    studyId: string,
    params?: KRIAlertListParams,
  ): Promise<KRIAlertListResponse> {
    const response = await httpCoreService.get<KRIAlertListResponse>(
      `/studies/${studyId}/rbqm/kri-alerts`,
      { params },
    );
    assertSuccess(response, "Failed to load KRI alerts");
    return response.data;
  }

  async updateKRIAlert(
    studyId: string,
    alertId: string,
    data: UpdateKRIAlertRequest,
  ): Promise<void> {
    const response = await httpCoreService.patch<null>(
      `/studies/${studyId}/rbqm/kri-alerts/${alertId}`,
      data,
    );
    assertRequestSuccess(response, "Failed to update KRI alert");
  }

  // ─── Monitoring Queue ──────────────────────────────────────────────────────

  async getMonitoringQueue(
    studyId: string,
    params?: MonitoringQueueParams,
  ): Promise<MonitoringQueueResponse> {
    const response = await httpCoreService.get<MonitoringQueueResponse>(
      `/studies/${studyId}/rbqm/monitoring-queue`,
      { params },
    );
    assertSuccess(response, "Failed to load monitoring queue");
    return response.data;
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  async createAction(
    studyId: string,
    data: CreateActionRequest,
  ): Promise<CreateActionResponse> {
    const response = await httpCoreService.post<CreateActionResponse>(
      `/studies/${studyId}/rbqm/actions`,
      data,
    );
    assertSuccess(response, "Failed to create action");
    return response.data;
  }

  // ─── KRI Configuration ─────────────────────────────────────────────────────

  async getKRIConfigurations(studyId: string): Promise<KRIConfiguration[]> {
    const response = await httpCoreService.get<KRIConfiguration[]>(
      `/studies/${studyId}/rbqm/kri-configurations`,
    );
    assertSuccess(response, "Failed to load KRI configurations");
    return response.data;
  }

  async updateKRIConfiguration(
    studyId: string,
    configId: string,
    data: UpdateKRIConfigRequest,
  ): Promise<void> {
    const response = await httpCoreService.patch<null>(
      `/studies/${studyId}/rbqm/kri-configurations/${configId}`,
      data,
    );
    assertRequestSuccess(response, "Failed to update KRI configuration");
  }
}
